import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Check, Copy } from 'lucide-react';
import { apiService } from '../../services/api.js';
import { sendFaqFeedback } from '../../lib/api.js';
import { markdownToText } from '../../utils/markdownToText.js';

const ThumbsFeedback = ({
  reviewId,
  pageName,
  grantType,
  sessionId,
  contextTag,
  tokenHash,
  query,
  output,
  industry,
  onFeedbackSubmitted,
  answerText,
  copyTargetId,
  // New prop structure for Ask OPTRA
  questionText,
  answerData,
  // Accessible labels; the chat defaults read wrong on a results report, so pages can override.
  upLabel = 'Good response',
  downLabel = 'Bad response',
}) => {
  const [rating, setRating] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const canInteract = !isSubmitting;

  const submitFaqFeedback = async (feedbackId, vote, comment = null, user_id = null) => {
    try {
      const data = await sendFaqFeedback({
        id: feedbackId,
        vote,
        comment: comment || null,
        user_id: user_id || null,
      });
      return { success: true, data };
    } catch (error) {
      console.error('FAQ feedback error:', error);
      return {
        success: false,
        error: error?.response?.data?.detail || error?.message || 'Failed to submit feedback.'
      };
    }
  };

  const handleSubmit = async (selectedRating) => {
    // Optimistic UI: show selection immediately on click.
    setRating(selectedRating);
    setIsSubmitting(true);

    try {
      let response;
      
      // Determine the correct feedback approach
      if (answerData && questionText) {
        // New Ask OPTRA flow: submit to FAQ feedback with proper trace ID
        const traceId = answerData.correlation_id || answerData.trace_id || 'unknown';
        console.log('[ThumbsFeedback] Submitting Ask OPTRA feedback:', {
          traceId,
          questionText,
          rating: selectedRating
        });
        
        // Fourth arg is user_id on the API; do not pass the full answer (was stored as session_id server-side).
        response = await submitFaqFeedback(traceId, selectedRating, questionText, null);
      } else if (contextTag === 'faq' && reviewId) {
        // Legacy FAQ flow
        response = await submitFaqFeedback(reviewId, selectedRating, query, answerText);
      } else {
        // Use general feedback for other contexts
        response = await apiService.submitGeneralFeedback({
          reviewId,
          pageName,
          grantType,
          rating: selectedRating,
          sessionId,
          contextTag,
          tokenHash,
          query,
          output,
          industry
        });
      }

      const success = Boolean(
        response?.success === true ||
        response?.ok === true ||
        (typeof response?.id === 'string' && response.id.trim().length > 0) ||
        (typeof response?.feedback_id === 'string' && response.feedback_id.trim().length > 0) ||
        (typeof response?.response_id === 'string' && response.response_id.trim().length > 0) ||
        (typeof response?.review_id === 'string' && response.review_id.trim().length > 0) ||
        (typeof response?.status === 'string' && ['ok', 'success', 'submitted'].includes(response.status.toLowerCase()))
      );

      if (success) {
        setRating(selectedRating);
        onFeedbackSubmitted?.(true, { rating: selectedRating, response });
        console.log('[ThumbsFeedback] Feedback submitted successfully');
      } else {
        throw new Error(response.message || 'Feedback submission failed');
      }
    } catch (error) {
      console.error('[ThumbsFeedback] Submission error:', error);
      // Keep the selected visual state even if submission fails,
      // so the control feels responsive to the click.
      onFeedbackSubmitted?.(false, { rating: selectedRating, error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    let cleanText = '';

    // Preferred: copy exactly what is rendered in the answer box.
    if (copyTargetId && typeof document !== 'undefined') {
      const target = document.getElementById(copyTargetId);
      if (target) {
        cleanText = (target.innerText || '').trim();
      }
    }

    // Fallback: convert markdown payload if rendered element is unavailable.
    if (!cleanText) {
      const rawText = answerText || answerData?.answer || answerData?.message_markdown || '';
      if (!rawText) return;
      cleanText = markdownToText(rawText);
    }
    
    try {
      await navigator.clipboard.writeText(cleanText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-4" style={{ color: '#4B5563' }}>
      {/* Thumbs feedback */}
      <div className="flex items-center gap-1">
        <button
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!canInteract) return;
            handleSubmit('up');
          }}
          aria-disabled={!canInteract}
          className={`thumb-feedback-btn inline-flex items-center justify-center p-1.5 ${rating === 'up' ? 'thumb-feedback-btn-active' : ''}`}
          aria-label={upLabel}
          title={upLabel}
          style={{
            opacity: 1,
            cursor: canInteract ? 'pointer' : 'not-allowed',
            pointerEvents: 'auto',
            zIndex: 1,
            backgroundColor: 'transparent',
            borderRadius: '9999px',
          }}
          type="button"
        >
          <ThumbsUp 
            size={16} 
            style={{
              color: '#60A5FA',
              fill: rating === 'up' ? 'currentColor' : 'none',
              stroke: 'currentColor',
              transition: 'none',
            }}
          />
        </button>
        <button
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!canInteract) return;
            handleSubmit('down');
          }}
          aria-disabled={!canInteract}
          className={`thumb-feedback-btn inline-flex items-center justify-center p-1.5 ${rating === 'down' ? 'thumb-feedback-btn-active' : ''}`}
          aria-label={downLabel}
          title={downLabel}
          style={{
            opacity: 1,
            cursor: canInteract ? 'pointer' : 'not-allowed',
            pointerEvents: 'auto',
            zIndex: 1,
            backgroundColor: 'transparent',
            borderRadius: '9999px',
          }}
          type="button"
        >
          <ThumbsDown 
            size={16} 
            style={{
              color: '#60A5FA',
              fill: rating === 'down' ? 'currentColor' : 'none',
              stroke: 'currentColor',
              transition: 'none',
            }}
          />
        </button>
      </div>

      {/* Copy button - aligned to the right, only show if there's text to copy */}
      {(answerText || answerData?.answer || answerData?.message_markdown) && (
        <div className="ml-auto">
          <button
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleCopy();
            }}
            className="feedback-icon-btn p-1.5 transition-colors"
            aria-label="Copy response"
            title="Copy response"
            type="button"
          >
            {copied ? (
              <Check 
                size={14} 
                className="fill-current"
                style={{ color: '#60A5FA' }}
              />
            ) : (
              <Copy 
                size={14}
                style={{ color: '#60A5FA' }}
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ThumbsFeedback;
